import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaEye,
  FaPhone,
  FaEnvelope,
  FaCar,
  FaCalendarAlt,
  FaFileAlt,
} from "react-icons/fa";
import {
  getAllDrivers,
  toggleDriverApproval,
  verifyDriverDocument,
  reset,
} from "../../features/admin/adminSlice";

const NewDriversList = () => {
  const dispatch = useDispatch();
  const { drivers, loading, error, success, message } = useSelector(
    (state) => state.admin,
  );
  const [newDrivers, setNewDrivers] = useState([]);

  // Only fetch drivers once when component mounts
  const hasFetchedDrivers = useRef(false);

  useEffect(() => {
    if (!hasFetchedDrivers.current && !loading) {
      hasFetchedDrivers.current = true;
      dispatch(getAllDrivers());
    }

    return () => {
      dispatch(reset());
    };
  }, [dispatch, loading]);

  // Handle success/error states with a ref to prevent infinite loops
  const hasHandledSuccess = useRef(false);

  useEffect(() => {
    if (error) {
      toast.error(error, { toastId: "admin-error" });
      dispatch(reset());
    }

    if (success && message && !hasHandledSuccess.current) {
      hasHandledSuccess.current = true;
      toast.success(message, { toastId: "admin-success" });
      dispatch(reset());

      // Only refresh if it's a driver-related success (not initial load)
      if (message.includes("approved") || message.includes("verified") || message.includes("updated")) {
        setTimeout(() => {
          dispatch(getAllDrivers());
          hasHandledSuccess.current = false;
        }, 1000);
      } else {
        hasHandledSuccess.current = false;
      }
    }
  }, [error, success, message, dispatch]);

  useEffect(() => {
    if (drivers) {
      // Filter for newly registered drivers (not approved and registered within last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const filteredNewDrivers = drivers.filter((driver) => {
        const isNotApproved = !driver.isApproved;
        const registrationDate = new Date(driver.createdAt || driver.registeredAt);
        const isRecent = registrationDate >= thirtyDaysAgo;
        
        return isNotApproved && isRecent;
      });

      // Sort by registration date (newest first)
      filteredNewDrivers.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.registeredAt);
        const dateB = new Date(b.createdAt || b.registeredAt);
        return dateB - dateA;
      });

      setNewDrivers(filteredNewDrivers);
    }
  }, [drivers]);

  const handleApproveDriver = (driverId, currentApprovalStatus) => {
    dispatch(
      toggleDriverApproval({
        driverId,
        isApproved: !currentApprovalStatus,
      }),
    );
  };

  const handleVerifyDocuments = (driverId) => {
    dispatch(verifyDriverDocument({ driverId, documentKey: null, isApproved: true }));
    toast.info("Verifying all documents...");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getDriverName = (driver) => {
    return driver.user?.name || driver.name || "Unknown Driver";
  };

  const getDriverPhone = (driver) => {
    return driver.user?.phone || driver.phone || "N/A";
  };

  const getDriverEmail = (driver) => {
    return driver.user?.email || driver.email || "N/A";
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        <span className="ml-3 text-gray-600">Loading new drivers...</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            New Driver Registrations
          </h3>
          <span className="bg-yellow-100 text-yellow-800 text-sm font-medium px-2.5 py-0.5 rounded-full">
            {newDrivers.length} Pending
          </span>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Drivers registered in the last 30 days requiring approval
        </p>
      </div>

      {newDrivers.length === 0 ? (
        <div className="px-6 py-8 text-center">
          <FaCheckCircle className="mx-auto h-12 w-12 text-green-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No pending registrations
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            All recent driver registrations have been processed.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-gray-200">
          {newDrivers.map((driver) => (
            <div key={driver._id} className="px-6 py-4 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-700">
                          {getDriverName(driver).charAt(0).toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {getDriverName(driver)}
                      </p>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span className="flex items-center">
                          <FaPhone className="mr-1 h-3 w-3" />
                          {getDriverPhone(driver)}
                        </span>
                        <span className="flex items-center">
                          <FaEnvelope className="mr-1 h-3 w-3" />
                          {getDriverEmail(driver)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                    <span className="flex items-center">
                      <FaCar className="mr-1 h-3 w-3" />
                      {driver.vehicleModel} ({driver.vehicleNumber})
                    </span>
                    <span className="flex items-center">
                      <FaCalendarAlt className="mr-1 h-3 w-3" />
                      Registered: {formatDate(driver.createdAt || driver.registeredAt)}
                    </span>
                    <span className="flex items-center">
                      <FaFileAlt className="mr-1 h-3 w-3" />
                      Docs: {driver.documentsVerified ? "Verified" : "Pending"}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  {!driver.documentsVerified && (
                    <button
                      onClick={() => handleVerifyDocuments(driver._id)}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <FaEye className="mr-1 h-3 w-3" />
                      Verify Docs
                    </button>
                  )}
                  
                  <button
                    onClick={() => handleApproveDriver(driver._id, driver.isApproved)}
                    className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    <FaCheckCircle className="mr-1 h-3 w-3" />
                    Approve
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NewDriversList;
